// @djsem/analysis-contract — AI Stem Separation / Source Separation Engine (Prompt 19A).
//
// Provider-agnostic AI stem separation. NO model execution here (rule: no separation in
// contract layer) — only job planning, credit/rights gating, provider dispatch, quality
// reporting, and handoff to StemIntelligence. Fails closed: no fake stems on failure.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult, stableStringify, fnv1a, type ProviderRegistry } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
import type { DJSEMAIProviderRegistry, DJSEMPlanTier, AIResolveResult } from "@djsem/shared";
import type { DJSEMStemRole } from "./importAdapters";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ---- modes ------------------------------------------------------------------

/** Which stem-separation split to perform (2/4/5-stem or a custom provider-defined split). */
export type DJSEMStemSeparationMode =
  | "two_stem_vocals_instrumental"
  | "four_stem_drums_bass_vocals_other"
  | "five_stem_drums_bass_vocals_melody_other"
  | "custom_provider";

const MODE_ROLES: Record<DJSEMStemSeparationMode, DJSEMStemRole[]> = {
  two_stem_vocals_instrumental: ["vocal", "other"],
  four_stem_drums_bass_vocals_other: ["drums", "bass", "vocal", "other"],
  five_stem_drums_bass_vocals_melody_other: ["drums", "bass", "vocal", "synth", "other"],
  custom_provider: ["other"],
};

// ---- provider abstraction ---------------------------------------------------

/** Runtime backing a stem-separation provider. */
export type DJSEMStemSeparationProviderType = "local_runner" | "external_service" | "manual_stub" | "disabled";

/** Describes what a stem-separation provider supports (modes, GPU/CPU, formats, limits). */
export interface DJSEMStemSeparationProviderCapabilities {
  providerId: string;
  providerType: DJSEMStemSeparationProviderType;
  label: string;
  supportedModes: DJSEMStemSeparationMode[];
  supportsGpu: boolean;
  supportsCpu: boolean;
  supportsPreview: boolean;
  supportsBatch: boolean;
  maxInputDurationMs?: number;
  maxInputFileSizeBytes?: number;
  outputFormats: Array<"wav" | "flac" | "mp3" | "ogg">;
  warnings: string[];
}

/** Input passed to a provider's estimate() call. */
export interface DJSEMStemSeparationEstimateInput {
  requestId: string;
  mode: DJSEMStemSeparationMode;
  qualityMode: "preview" | "standard" | "high";
  sourceDurationMs?: number;
}

/** Input passed to a provider's run() call to perform the actual separation. */
export interface DJSEMStemSeparationRunnerInput {
  requestId: string; jobId: string;
  trackId: string;
  sourceAudioRef: string;
  mode: DJSEMStemSeparationMode;
  outputFormat: "wav" | "flac" | "mp3" | "ogg";
  qualityMode: "preview" | "standard" | "high";
}

/** Result of a provider's run() call: produced stems plus partial/warning/error state. */
export interface DJSEMStemSeparationRunnerResult {
  ok: boolean;
  stems: Array<{
    role: DJSEMStemRole;
    label: string;
    fileRef: string;
    r2Key?: string;
    hash?: string;
    durationMs?: number;
    alignmentOffsetMs?: number;
    confidence: number;
  }>;
  creditCostCharged?: number;
  partial: boolean;
  warnings: string[];
  errors: string[];
}

/** Interface every stem-separation provider (real or stub) must implement. */
export interface DJSEMStemSeparationProvider {
  id: string;
  type: DJSEMStemSeparationProviderType;
  capabilities: DJSEMStemSeparationProviderCapabilities;
  estimate(input: DJSEMStemSeparationEstimateInput): Promise<DJSEMStemSeparationEstimate>;
  run(input: DJSEMStemSeparationRunnerInput): Promise<DJSEMStemSeparationRunnerResult>;
  validateInput(input: DJSEMStemSeparationRunnerInput): DJSEMValidationResult;
}

/** Criteria used to pick a stem-separation provider capable of servicing a request. */
export interface SelectStemSeparationProviderInput {
  mode: DJSEMStemSeparationMode;
  outputFormat?: "wav" | "flac" | "mp3" | "ogg";
  preferredProviderId?: string;
}

// ---- contracts --------------------------------------------------------------

/** Lifecycle status of a stem-separation job. */
export type DJSEMStemSeparationJobStatus =
  | "created" | "awaiting_rights_confirmation" | "awaiting_credits"
  | "queued" | "running" | "completed" | "failed" | "cancelled" | "expired";

/** A user/system request to separate a track's source audio into stems. */
export interface DJSEMStemSeparationRequest {
  id: string;
  projectId?: string;
  trackId: string;
  sourceAudioRef: string;
  sourceAudioHash?: string;
  requestedMode: DJSEMStemSeparationMode;
  providerId?: string;
  /** Model-level provenance (Prompt 140/141) — which registry model this request prefers/resolved to. */
  modelId?: string;
  outputFormat: "wav" | "flac" | "mp3" | "ogg";
  qualityMode: "preview" | "standard" | "high";
  userConfirmedRightsWarning: boolean;
  userConfirmedCreditUsage: boolean;
  createdBy?: string;
  createdAt: string;
}

/** A provider's estimate of credit cost/duration/storage for a not-yet-run separation request. */
export interface DJSEMStemSeparationEstimate {
  requestId: string;
  estimatedCreditCost: number;
  estimatedDurationSeconds?: number;
  estimatedOutputStorageBytes?: number;
  providerId?: string;
  warnings: string[];
  blockers: string[];
}

/** A queued/running/completed unit of work backing a stem-separation request. */
export interface DJSEMStemSeparationJob {
  id: string;
  requestId: string;
  projectId?: string;
  trackId: string;
  sourceAudioRef: string;
  mode: DJSEMStemSeparationMode;
  providerId: string;
  /** Model-level provenance (Prompt 140/141) — resolved via DJSEMAIProviderRegistry where available. */
  modelId?: string;
  status: DJSEMStemSeparationJobStatus;
  progress: number;
  creditCostEstimated: number;
  creditCostCharged?: number;
  outputStemIds: string[];
  outputAssetIds: string[];
  qualityReportId?: string;
  warnings: string[];
  errors: string[];
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  expiresAt?: string;
}

/** A single separated stem produced by a completed stem-separation job. */
export interface DJSEMSeparatedStemOutput {
  id: string;
  jobId: string;
  trackId: string;
  role: DJSEMStemRole;
  label: string;
  fileRef: string;
  r2Key?: string;
  hash?: string;
  format: "wav" | "flac" | "mp3" | "ogg";
  durationMs?: number;
  alignedToMaster: boolean;
  alignmentOffsetMs?: number;
  confidence: number;
  source: "ai_separation";
  providerId: string;
  /** Model-level provenance (Prompt 140/141) — carried over from the job that produced this stem. */
  modelId?: string;
  warnings: string[];
}

/** A per-stem quality/confidence report for a completed separation job, gating user review. */
export interface DJSEMStemSeparationQualityReport {
  id: string;
  jobId: string;
  providerId: string;
  createdAt: string;
  overallConfidence: number;
  stemReports: Array<{
    stemOutputId: string;
    role: DJSEMStemRole;
    confidence: number;
    bleedRisk: "low" | "medium" | "high" | "unknown";
    artifactRisk: "low" | "medium" | "high" | "unknown";
    silenceRisk: "low" | "medium" | "high" | "unknown";
    warnings: string[];
  }>;
  userReviewStatus: "not_reviewed" | "needs_review" | "reviewed" | "approved" | "rejected";
  warnings: string[];
}

/** A rights-related warning attached to a stem-separation request/job. */
export interface DJSEMRightsWarning {
  id: string;
  warningType: string;
  message: string;
  requiresConfirmation: boolean;
}

// ---- service inputs ---------------------------------------------------------

/** Input for {@link StemSeparationService.createRequest}. */
export interface CreateStemSeparationRequestInput extends Omit<DJSEMStemSeparationRequest, "id" | "createdAt"> {
  id?: string; createdAt?: string;
}
/** Input for {@link StemSeparationService.estimateRequest}. */
export interface EstimateStemSeparationInput { request: DJSEMStemSeparationRequest; providerId: string; sourceDurationMs?: number; }
/** Input for {@link StemSeparationService.createJob}. */
export interface CreateStemSeparationJobInput { request: DJSEMStemSeparationRequest; providerId: string; modelId?: string; creditCostEstimated: number; createdAt?: string; }
/** Input for {@link StemSeparationService.queueJob}. */
export interface QueueStemSeparationJobInput { job: DJSEMStemSeparationJob; }
/** Input for {@link StemSeparationService.updateJobProgress}. */
export interface UpdateStemSeparationProgressInput { job: DJSEMStemSeparationJob; progress: number; }
/** Input for {@link StemSeparationService.completeJob}. */
export interface CompleteStemSeparationJobInput { job: DJSEMStemSeparationJob; result: DJSEMStemSeparationRunnerResult; completedAt?: string; }
/** Input for {@link StemSeparationService.failJob}. */
export interface FailStemSeparationJobInput { job: DJSEMStemSeparationJob; error: string; failedAt?: string; }
/** Input for {@link StemSeparationService.cancelJob}. */
export interface CancelStemSeparationJobInput { job: DJSEMStemSeparationJob; }
/** Input for {@link StemSeparationService.createQualityReport}. */
export interface CreateStemQualityReportInput { job: DJSEMStemSeparationJob; outputs: DJSEMSeparatedStemOutput[]; createdAt?: string; }
/** Input for {@link StemSeparationService.handoffToStemIntelligence}. */
export interface HandoffSeparatedStemsInput { job: DJSEMStemSeparationJob; outputs: DJSEMSeparatedStemOutput[]; }

/** Input for {@link StemSeparationPolicyService.checkCreditAvailability}. */
export interface StemSeparationCreditCheckInput { request: DJSEMStemSeparationRequest; creditsRemaining: number; estimatedCredits: number; }
/** Input for {@link StemSeparationPolicyService.estimateCreditCost}. */
export interface StemSeparationCreditEstimateInput { request: DJSEMStemSeparationRequest; sourceDurationMs?: number; }
/** Input for {@link StemSeparationPolicyService.createRightsWarning}. */
export interface StemSeparationRightsWarningInput { request: DJSEMStemSeparationRequest; }

// ---- providers --------------------------------------------------------------

/** Always-fails provider used when AI stem separation is not enabled in this environment. */
export class DisabledStemSeparationProvider implements DJSEMStemSeparationProvider {
  id = "disabled";
  type: DJSEMStemSeparationProviderType = "disabled";
  capabilities: DJSEMStemSeparationProviderCapabilities = {
    providerId: "disabled", providerType: "disabled", label: "Disabled",
    supportedModes: [], supportsGpu: false, supportsCpu: false,
    supportsPreview: false, supportsBatch: false, outputFormats: [],
    warnings: ["AI stem separation is not enabled in this environment."],
  };
  async estimate(input: DJSEMStemSeparationEstimateInput): Promise<DJSEMStemSeparationEstimate> {
    return { requestId: input.requestId, estimatedCreditCost: 0, providerId: this.id, warnings: this.capabilities.warnings, blockers: ["provider disabled"] };
  }
  async run(): Promise<DJSEMStemSeparationRunnerResult> {
    return { ok: false, stems: [], partial: false, warnings: [], errors: ["AI stem separation is disabled."] };
  }
  validateInput(): DJSEMValidationResult { return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: "provider disabled" }] }, "stem_sep_disabled"); }
}

/** Deterministic placeholder provider for tests/offline UI — produces no real audio separation. */
export class ManualStubStemSeparationProvider implements DJSEMStemSeparationProvider {
  id = "manual_stub_stemsep";
  type: DJSEMStemSeparationProviderType = "manual_stub";
  capabilities: DJSEMStemSeparationProviderCapabilities = {
    providerId: "manual_stub_stemsep", providerType: "manual_stub", label: "Manual Stub",
    supportedModes: ["two_stem_vocals_instrumental", "four_stem_drums_bass_vocals_other", "five_stem_drums_bass_vocals_melody_other"],
    supportsGpu: false, supportsCpu: true, supportsPreview: true, supportsBatch: false,
    maxInputDurationMs: 600000, outputFormats: ["wav", "flac"],
    warnings: ["Manual stub provider — for tests and honest offline UI only. Produces placeholder stem metadata, no real audio separation."],
  };
  async estimate(input: DJSEMStemSeparationEstimateInput): Promise<DJSEMStemSeparationEstimate> {
    const dur = input.sourceDurationMs ?? 180000;
    const qMult = input.qualityMode === "high" ? 3 : input.qualityMode === "standard" ? 2 : 1;
    return { requestId: input.requestId, estimatedCreditCost: Math.ceil(dur / 60000) * qMult, estimatedDurationSeconds: Math.ceil(dur / 1000 / 4), providerId: this.id, warnings: [], blockers: [] };
  }
  async run(input: DJSEMStemSeparationRunnerInput): Promise<DJSEMStemSeparationRunnerResult> {
    const roles = MODE_ROLES[input.mode];
    const stems = roles.map((role, i) => ({
      role, label: `${role}_stem`,
      fileRef: `stub://stems/${input.jobId}/${role}.${input.outputFormat}`,
      hash: fnv1a(`${input.jobId}_${role}`),
      durationMs: 180000, alignmentOffsetMs: 0, confidence: 0.75,
    }));
    return { ok: true, stems, creditCostCharged: roles.length, partial: false, warnings: this.capabilities.warnings, errors: [] };
  }
  validateInput(input: DJSEMStemSeparationRunnerInput): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!input.sourceAudioRef) issues.push({ path: "$.sourceAudioRef", message: "required" });
    if (!this.capabilities.supportedModes.includes(input.mode)) issues.push({ path: "$.mode", message: `unsupported mode for ${this.id}` });
    if (!this.capabilities.outputFormats.includes(input.outputFormat)) issues.push({ path: "$.outputFormat", message: "unsupported format" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "stem_sep_stub_input");
  }
}

// ---- provider registry ------------------------------------------------------

/** Registry of stem-separation providers; selects the best match for a request, failing closed to "disabled". */
export class StemSeparationProviderRegistry implements ProviderRegistry<DJSEMStemSeparationProvider, DJSEMStemSeparationProviderCapabilities, SelectStemSeparationProviderInput> {
  private providers = new Map<string, DJSEMStemSeparationProvider>();
  constructor() {
    this.register(new DisabledStemSeparationProvider());
    this.register(new ManualStubStemSeparationProvider());
  }
  register(provider: DJSEMStemSeparationProvider): void { this.providers.set(provider.id, provider); }
  getProvider(providerId: string): DJSEMStemSeparationProvider | undefined { return this.providers.get(providerId); }
  listProviders(): DJSEMStemSeparationProviderCapabilities[] { return [...this.providers.values()].map((p) => p.capabilities); }
  selectProvider(input: SelectStemSeparationProviderInput): DJSEMStemSeparationProvider {
    if (input.preferredProviderId) {
      const p = this.providers.get(input.preferredProviderId);
      if (p && p.capabilities.supportedModes.includes(input.mode)) return p;
    }
    for (const p of this.providers.values()) {
      if (p.type === "disabled") continue;
      if (!p.capabilities.supportedModes.includes(input.mode)) continue;
      if (input.outputFormat && !p.capabilities.outputFormats.includes(input.outputFormat)) continue;
      return p;
    }
    return this.providers.get("disabled")!; // fail closed
  }
}

// ---- global AI model registry bridge (Prompt 140/141) ----------------------

/** Input for {@link resolveStemSeparationModel}. */
export interface ResolveStemSeparationModelInput {
  plan: DJSEMPlanTier;
  preferredModelId?: string;
  requireSafePublicOutput?: boolean;
}

/**
 * Resolve a stem-separation model via the global DJSEMAIProviderRegistry (Prompt 66/140/141)
 * — plan-tier and safety-aware, fails closed (ok:false) if nothing is eligible. This is
 * additive: it does not replace StemSeparationProviderRegistry.selectProvider, which remains
 * the fail-closed local runner-selection path (disabled/manual_stub) used when no global
 * registry is available or when nothing resolves.
 */
export function resolveStemSeparationModel(
  modelRegistry: DJSEMAIProviderRegistry,
  input: ResolveStemSeparationModelInput,
): AIResolveResult {
  return modelRegistry.resolve({
    engine: "stem_separation",
    plan: input.plan,
    preferredModelId: input.preferredModelId,
    requireSafePublicOutput: input.requireSafePublicOutput,
  });
}

// ---- policy service ---------------------------------------------------------

/** Rights confirmation and credit-availability gating for stem separation requests. */
export class StemSeparationPolicyService {
  /**
   * Check that the user has confirmed rights to separate the source audio.
   * @param request - the request to check.
   * @returns validation result (invalid when rights confirmation is missing).
   */
  checkRightsConfirmation(request: DJSEMStemSeparationRequest): DJSEMValidationResult {
    if (!request.userConfirmedRightsWarning) return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.userConfirmedRightsWarning", message: "user must confirm they hold rights to separate this audio" }] }, "stem_sep_rights");
    return toDJSEMValidationResult({ valid: true, issues: [] }, "stem_sep_rights");
  }

  /**
   * Check that credit usage was confirmed and enough credits remain.
   * @param input - the request plus available/estimated credit counts.
   * @returns validation result (invalid when confirmation is missing or credits are insufficient).
   */
  checkCreditAvailability(input: StemSeparationCreditCheckInput): DJSEMValidationResult {
    if (!input.request.userConfirmedCreditUsage) return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.userConfirmedCreditUsage", message: "user must confirm credit usage" }] }, "stem_sep_credits");
    if (input.creditsRemaining < input.estimatedCredits) return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: `Insufficient credits: ${input.creditsRemaining}/${input.estimatedCredits}` }] }, "stem_sep_credits");
    return toDJSEMValidationResult({ valid: true, issues: [] }, "stem_sep_credits");
  }

  /**
   * Estimate the credit cost of a request based on duration, quality, and mode.
   * @param input - the request and optional known source duration.
   * @returns the estimated credit cost (minimum 1).
   */
  estimateCreditCost(input: StemSeparationCreditEstimateInput): number {
    const dur = input.sourceDurationMs ?? 180000;
    const qMult = input.request.qualityMode === "high" ? 3 : input.request.qualityMode === "standard" ? 2 : 1;
    const modeMult = input.request.requestedMode === "five_stem_drums_bass_vocals_melody_other" ? 1.5 : 1;
    return Math.max(1, Math.ceil((dur / 60000) * qMult * modeMult));
  }

  /**
   * Build the standard rights warning for stem separation (not legally cleared for redistribution).
   * @param input - the request the warning is attached to (unused, kept for interface symmetry).
   * @returns the rights warning record.
   */
  createRightsWarning(input: StemSeparationRightsWarningInput): DJSEMRightsWarning {
    return { id: `rw_${_seq.next()}`, warningType: "stem_separation_rights", message: "AI stem separation should only be used on audio you own or have the rights to process. Separated stems are not legally cleared for redistribution.", requiresConfirmation: true };
  }
}

// ---- main service -----------------------------------------------------------

const STATUS_TRANSITIONS: Record<DJSEMStemSeparationJobStatus, DJSEMStemSeparationJobStatus[]> = {
  created: ["awaiting_rights_confirmation", "awaiting_credits", "queued", "cancelled"],
  awaiting_rights_confirmation: ["queued", "cancelled"],
  awaiting_credits: ["queued", "cancelled"],
  queued: ["running", "cancelled"],
  running: ["completed", "failed", "cancelled"],
  completed: ["expired"],
  failed: [],
  cancelled: [],
  expired: [],
};

/** Orchestrates stem-separation requests through validation, estimation, job execution, and StemIntelligence handoff. */
export class StemSeparationService {
  constructor(
    private registry: StemSeparationProviderRegistry,
    private _policy = new StemSeparationPolicyService(),
    private stemIntelligence?: { registerSeparatedStems?: (stems: DJSEMSeparatedStemOutput[]) => unknown }
  ) {}

  /** The policy service used for rights/credit gating. */
  get policy(): StemSeparationPolicyService { return this._policy; }

  /**
   * Build a new stem-separation request, filling in id/createdAt defaults.
   * @param input - the request fields (id/createdAt optional).
   * @returns the constructed request.
   */
  createRequest(input: CreateStemSeparationRequestInput): DJSEMStemSeparationRequest {
    return { ...input, id: input.id ?? `ssr_${input.trackId}_${_seq.next()}`, createdAt: input.createdAt ?? AT };
  }

  /**
   * Validate a request's required fields and confirmations.
   * @param request - the request to validate.
   * @returns validation result.
   */
  validateRequest(request: DJSEMStemSeparationRequest): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!request.trackId) issues.push({ path: "$.trackId", message: "required" });
    if (!request.sourceAudioRef) issues.push({ path: "$.sourceAudioRef", message: "required" });
    if (!request.requestedMode) issues.push({ path: "$.requestedMode", message: "required" });
    if (!request.userConfirmedRightsWarning) issues.push({ path: "$.userConfirmedRightsWarning", message: "rights confirmation required" });
    if (!request.userConfirmedCreditUsage) issues.push({ path: "$.userConfirmedCreditUsage", message: "credit confirmation required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "stem_sep_request");
  }

  /**
   * Ask the selected provider to estimate credit cost/duration for a request.
   * @param input - the request, provider id, and optional known source duration.
   * @returns the provider's estimate (falls back to the disabled provider if the id is unknown).
   */
  async estimateRequest(input: EstimateStemSeparationInput): Promise<DJSEMStemSeparationEstimate> {
    const provider = this.registry.getProvider(input.providerId) ?? this.registry.getProvider("disabled")!;
    return provider.estimate({ requestId: input.request.id, mode: input.request.requestedMode, qualityMode: input.request.qualityMode, sourceDurationMs: input.sourceDurationMs });
  }

  /**
   * Create a job for a request, parked at the appropriate status if rights/credit confirmations are missing.
   * @param input - the request, provider id, model id, and estimated credit cost.
   * @returns the created job.
   */
  createJob(input: CreateStemSeparationJobInput): DJSEMStemSeparationJob {
    const status: DJSEMStemSeparationJobStatus = !input.request.userConfirmedRightsWarning
      ? "awaiting_rights_confirmation"
      : !input.request.userConfirmedCreditUsage ? "awaiting_credits" : "created";
    return {
      id: `ssj_${input.request.id}_${fnv1a(stableStringify({ p: input.providerId }))}`,
      requestId: input.request.id, projectId: input.request.projectId,
      trackId: input.request.trackId, sourceAudioRef: input.request.sourceAudioRef,
      mode: input.request.requestedMode, providerId: input.providerId,
      modelId: input.modelId ?? input.request.modelId,
      status, progress: 0, creditCostEstimated: input.creditCostEstimated,
      outputStemIds: [], outputAssetIds: [], warnings: [], errors: [],
      createdAt: input.createdAt ?? AT,
    };
  }

  private canTransition(from: DJSEMStemSeparationJobStatus, to: DJSEMStemSeparationJobStatus): boolean {
    return (STATUS_TRANSITIONS[from] ?? []).includes(to);
  }

  /**
   * Transition a job to "queued" if its current status allows it.
   * @param input - the job to queue.
   * @returns the (possibly) updated job.
   */
  async queueJob(input: QueueStemSeparationJobInput): Promise<DJSEMStemSeparationJob> {
    if (this.canTransition(input.job.status, "queued")) return { ...input.job, status: "queued" };
    return input.job;
  }

  /**
   * Update a job's progress (monotonically) and move "queued" jobs into "running".
   * @param input - the job and its new progress value (clamped to 0..1).
   * @returns the updated job.
   */
  updateJobProgress(input: UpdateStemSeparationProgressInput): DJSEMStemSeparationJob {
    const clamped = Math.max(0, Math.min(1, input.progress));
    const progress = Math.max(input.job.progress, clamped); // monotonic
    const status = input.job.status === "queued" ? "running" : input.job.status;
    return { ...input.job, progress, status };
  }

  /**
   * Mark a job completed or failed based on the provider result. Fails closed: a result with no stems fails the job.
   * @param input - the job and the provider's separation result.
   * @returns the updated job.
   */
  completeJob(input: CompleteStemSeparationJobInput): DJSEMStemSeparationJob {
    // Fail closed: no fake stems.
    if (!input.result.ok || input.result.stems.length === 0) {
      return { ...input.job, status: "failed", progress: 1, errors: [...input.job.errors, ...input.result.errors, "No stems produced — job failed."], completedAt: input.completedAt ?? AT };
    }
    const warnings = [...input.job.warnings, ...input.result.warnings];
    if (input.result.partial) warnings.push("Partial separation — some stems may be missing or low quality.");
    return {
      ...input.job, status: "completed", progress: 1,
      creditCostCharged: input.result.creditCostCharged ?? input.job.creditCostEstimated,
      warnings, completedAt: input.completedAt ?? AT,
    };
  }

  /**
   * Mark a job failed with an explicit error.
   * @param input - the job and the failure reason.
   * @returns the updated (failed) job.
   */
  failJob(input: FailStemSeparationJobInput): DJSEMStemSeparationJob {
    return { ...input.job, status: "failed", errors: [...input.job.errors, input.error], completedAt: input.failedAt ?? AT };
  }

  /**
   * Cancel a job unless it has already reached a terminal state.
   * @param input - the job to cancel.
   * @returns the updated job (unchanged if already completed/failed).
   */
  cancelJob(input: CancelStemSeparationJobInput): DJSEMStemSeparationJob {
    if (input.job.status === "completed" || input.job.status === "failed") return input.job;
    return { ...input.job, status: "cancelled" };
  }

  /**
   * Convert a provider run result's raw stems into tracked {@link DJSEMSeparatedStemOutput} records.
   * @param job - the job the stems belong to.
   * @param result - the provider's run result.
   * @returns the constructed separated-stem output records.
   */
  buildOutputs(job: DJSEMStemSeparationJob, result: DJSEMStemSeparationRunnerResult): DJSEMSeparatedStemOutput[] {
    return result.stems.map((s, i) => ({
      id: `sso_${job.id}_${i}`, jobId: job.id, trackId: job.trackId,
      role: s.role, label: s.label, fileRef: s.fileRef, r2Key: s.r2Key, hash: s.hash,
      format: (job.sourceAudioRef.split(".").pop() as "wav") ?? "wav",
      durationMs: s.durationMs,
      alignedToMaster: (s.alignmentOffsetMs ?? 0) === 0,
      alignmentOffsetMs: s.alignmentOffsetMs, confidence: s.confidence,
      source: "ai_separation" as const, providerId: job.providerId, modelId: job.modelId, warnings: [],
    }));
  }

  /**
   * Build a quality report from a job's separated-stem outputs, flagging low-confidence results for review.
   * @param input - the job, its output stems, and optional report creation time.
   * @returns the constructed quality report.
   */
  createQualityReport(input: CreateStemQualityReportInput): DJSEMStemSeparationQualityReport {
    const stemReports = input.outputs.map((o) => ({
      stemOutputId: o.id, role: o.role, confidence: o.confidence,
      bleedRisk: (o.confidence >= 0.8 ? "low" : o.confidence >= 0.6 ? "medium" : "high") as "low" | "medium" | "high" | "unknown",
      artifactRisk: (o.confidence >= 0.8 ? "low" : o.confidence >= 0.6 ? "medium" : "high") as "low" | "medium" | "high" | "unknown",
      silenceRisk: "unknown" as const,
      warnings: o.confidence < 0.6 ? ["Low confidence — review before use"] : [],
    }));
    const overall = input.outputs.length > 0 ? input.outputs.reduce((s, o) => s + o.confidence, 0) / input.outputs.length : 0;
    return {
      id: `sqr_${input.job.id}`, jobId: input.job.id, providerId: input.job.providerId,
      createdAt: input.createdAt ?? AT, overallConfidence: overall, stemReports,
      userReviewStatus: overall < 0.6 ? "needs_review" : "not_reviewed",
      warnings: overall < 0.6 ? ["Overall low separation confidence"] : [],
    };
  }

  /**
   * Hand off a completed job's separated stems to StemIntelligence so they become normal DJSEM stems for downstream use.
   * @param input - the job and its output stems.
   * @returns the outputs handed off, or an empty array if the job is not completed.
   */
  async handoffToStemIntelligence(input: HandoffSeparatedStemsInput): Promise<DJSEMSeparatedStemOutput[]> {
    if (input.job.status !== "completed") return [];
    if (this.stemIntelligence?.registerSeparatedStems) {
      this.stemIntelligence.registerSeparatedStems(input.outputs);
    }
    return input.outputs;
  }

  /**
   * Build the exact tuples DJSEMRightsLedgerService.recordAiGenerated (Prompt 65) needs for
   * each output stem — one ledger entry per stem, keyed by the stem output id as assetId.
   * Only produces entries for a completed job (fails closed: no ledger entries for
   * incomplete/failed jobs, matching "no fake stems" / no premature provenance claims).
   * The actual ledger write happens at the persistence/worker-route layer, which has the
   * DJSEMRightsLedgerService instance; this keeps the contract layer free of storage concerns.
   */
  buildRightsLedgerInputs(job: DJSEMStemSeparationJob, outputs: DJSEMSeparatedStemOutput[]): StemRightsLedgerInput[] {
    if (job.status !== "completed") return [];
    return outputs.map((o) => ({
      assetId: o.id,
      providerId: o.providerId,
      modelId: o.modelId ?? job.modelId ?? "unknown",
      promptRef: job.requestId,
    }));
  }
}

/** A single rights-ledger entry input, one per separated stem output. */
export interface StemRightsLedgerInput {
  assetId: string;
  providerId: string;
  modelId: string;
  promptRef?: string;
}

// ---- manifest (Prompt 139 §8 / 19A §14,16) ----------------------------------

/** Current schema version stamped onto every generated stem-separation manifest. */
export const STEM_SEPARATION_MANIFEST_SCHEMA_VERSION = "1.0.0";

/** A flat, hashable record of what a stem-separation job produced, by which provider/model. */
export interface DJSEMStemSeparationManifest {
  manifestId: string;
  schemaVersion: string;
  createdAt: string;
  jobId: string;
  requestId: string;
  trackId: string;
  sourceAudioRef: string;
  mode: DJSEMStemSeparationMode;
  providerId: string;
  modelId?: string;
  qualityReportId?: string;
  overallConfidence?: number;
  stems: Array<{
    stemOutputId: string;
    role: DJSEMStemRole;
    label: string;
    fileRef: string;
    r2Key?: string;
    format: "wav" | "flac" | "mp3" | "ogg";
    durationMs?: number;
    confidence: number;
    providerId: string;
    modelId?: string;
  }>;
  warnings: string[];
  errors: string[];
}

/**
 * Whether a manifest can honestly be generated for this job — fails closed: only a completed
 * job with at least one real output qualifies. No manifest for incomplete/failed/empty jobs
 * (matches the "no fake stems" rule — a manifest is a provenance claim, not a wish).
 */
export function canGenerateStemSeparationManifest(job: DJSEMStemSeparationJob, outputs: DJSEMSeparatedStemOutput[]): boolean {
  return job.status === "completed" && outputs.length > 0;
}

/**
 * Build the stem-separation job manifest (Prompt 19A §14/§16, Prompt 139 provenance fields) —
 * following the same shape/spirit as DJSEMRekordboxExportManifest (rekordboxExport.ts): a flat,
 * hashable-by-caller record of what was produced, by which provider/model, with what confidence.
 * Caller (worker route) must check canGenerateStemSeparationManifest first.
 */
export function generateStemSeparationManifest(
  job: DJSEMStemSeparationJob,
  outputs: DJSEMSeparatedStemOutput[],
  qualityReport?: DJSEMStemSeparationQualityReport,
): DJSEMStemSeparationManifest {
  return {
    manifestId: `ssm_${job.id}`,
    schemaVersion: STEM_SEPARATION_MANIFEST_SCHEMA_VERSION,
    createdAt: job.completedAt ?? job.createdAt,
    jobId: job.id, requestId: job.requestId, trackId: job.trackId, sourceAudioRef: job.sourceAudioRef,
    mode: job.mode, providerId: job.providerId, modelId: job.modelId, qualityReportId: job.qualityReportId,
    overallConfidence: qualityReport?.overallConfidence,
    stems: outputs.map((o) => ({
      stemOutputId: o.id, role: o.role, label: o.label, fileRef: o.fileRef, r2Key: o.r2Key,
      format: o.format, durationMs: o.durationMs, confidence: o.confidence,
      providerId: o.providerId, modelId: o.modelId,
    })),
    warnings: [...job.warnings, ...(qualityReport?.warnings ?? [])],
    errors: job.errors,
  };
}

export { MODE_ROLES as STEM_SEPARATION_MODE_ROLES };
